/**
 * One error type for everything the API can say.
 *
 * The backend answers every failure with `{ error: { code, message, details } }`
 * and puts a readable sentence in `message` — "That order is packing, it is not
 * ready to collect yet", "Incorrect code. 3 attempt(s) left." Replacing that
 * with a generic "Something went wrong" throws away the only part the rider can
 * act on, so the server's wording is kept and the screen decides on the `code`.
 */
export type ApiErrorBody = {
  error: { code: string; message: string; details?: unknown };
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  /** Signed in, but not entitled to this — someone else's delivery, chiefly. */
  get isForbidden() {
    return this.status === 403;
  }

  get isNotFound() {
    return this.status === 404;
  }

  /**
   * The job moved underneath us.
   *
   * Dispatch reassigned it, the pharmacy has not packed it yet, or the tap
   * landed twice. Every one of those means the screen is describing something
   * that is no longer true, so the caller refetches rather than leaving a
   * button that will fail the same way again.
   */
  get isStaleState() {
    return this.status === 409;
  }
}

/**
 * Nobody answered: DNS, a refused connection, a timeout. Saying so is more
 * useful than reporting a status of 0, and it is a different kind of problem
 * from the server refusing — one is weather, the other is an answer. A rider
 * spends the shift in lifts, basements and stairwells, so this is the common
 * failure here rather than the exceptional one.
 */
export class NetworkError extends Error {
  constructor(message = 'No connection. Move somewhere with signal and try again.') {
    super(message);
    this.name = 'NetworkError';
  }
}

/** The sentence to put on screen, whatever went wrong. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof NetworkError) return error.message;
  return 'Something went wrong. Try again.';
}
